import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Form, Input, Spin } from "antd";
import { resetMenu } from "../../lib/menuNavigation.js";
import { clearScreenState } from "../../lib/screenState.js";
import {
  acceptAuthInviteCallback,
  edge,
  hasAuthInviteCallback,
  query,
  requireClient,
  supabase,
} from "../../lib/supabase.js";

export function AuthGate({ children }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [invitePending, setInvitePending] = useState(hasAuthInviteCallback);
  const inviteActive = useRef(invitePending);
  const revision = useRef(0);
  const restore = useCallback(async () => {
    const current = ++revision.current;

    try {
      const client = requireClient();
      const { data, error: sessionError } = await client.auth.getSession();
      if (sessionError)
        throw new Error("세션을 복구하지 못했습니다. 다시 로그인해 주세요.");
      if (!data.session) {
        if (current === revision.current) {
          resetMenu();
          setContext(null);
        }
        return;
      }
      const verified = await edge("session-context", null, "GET");
      const [profile, academy] =
        verified.role === "kiosk"
          ? [null, null]
          : await Promise.all([
              query(
                client
                  .from("profiles")
                  .select("id,display_name,role,status")
                  .eq("id", verified.userId)
                  .single(),
              ),
              query(
                client
                  .from("academies")
                  .select("id,name,phone,address,timezone")
                  .eq("id", verified.academyId)
                  .single(),
              ),
            ]);
      if (current === revision.current) {
        setContext({ ...verified, profile, academy });
        setError("");
      }
    } catch (failure) {
      if (current === revision.current) {
        clearScreenState();
        setContext(null);
        setError(failure.message);
      }
    } finally {
      if (current === revision.current) setLoading(false);
    }
  }, []);
  useEffect(() => {
    let disposed = false;
    async function initialize() {
      if (!inviteActive.current) {
        await restore();
        return;
      }
      try {
        await acceptAuthInviteCallback();
        if (!disposed) {
          setError("");
          setLoading(false);
        }
      } catch (failure) {
        inviteActive.current = false;
        if (!disposed) {
          setInvitePending(false);
          setError(failure.message);
          setLoading(false);
        }
      }
    }
    initialize();
    const subscription = supabase?.auth.onAuthStateChange(() => {
      if (!inviteActive.current) setTimeout(restore, 0);
    });
    const onFocus = () => {
      if (!inviteActive.current) restore();
    };
    window.addEventListener("focus", onFocus);
    return () => {
      disposed = true;
      revision.current++;
      subscription?.data.subscription.unsubscribe();
      window.removeEventListener("focus", onFocus);
    };
  }, [restore]);
  async function setInitialPassword(values) {
    setLoading(true);
    setError("");
    try {
      const { error: failure } = await requireClient().auth.updateUser({
        password: values.password,
      });
      if (failure) throw failure;
      inviteActive.current = false;
      setInvitePending(false);
      await restore();
    } catch {
      setError("비밀번호를 설정하지 못했습니다. 초대 링크를 다시 확인해 주세요.");
      setLoading(false);
    }
  }
  async function login(values) {
    resetMenu();
    setLoading(true);
    setError("");
    try {
      const { error: failure } =
        await requireClient().auth.signInWithPassword(values);
      if (failure)
        throw new Error(
          "로그인하지 못했습니다. 이메일과 비밀번호를 확인해 주세요.",
        );
      await restore();
    } catch (failure) {
      setError(failure.message);
      setLoading(false);
    }
  }
  async function logout() {
    resetMenu();
    setContext(null);
    revision.current++;
    const { error: failure } = await requireClient().auth.signOut({
      scope: "global",
    });
    if (failure) {
      await supabase.auth.signOut({ scope: "local" });
      setError(
        "이 기기에서 로그아웃했습니다. 전체 세션 종료는 다시 시도해 주세요.",
      );
    }
  }
  if (loading)
    return (
      <div className="auth-page">
        <Spin tip="세션을 확인하고 있습니다." fullscreen />
      </div>
    );
  if (invitePending)
    return (
      <div className="auth-page">
        <Card
          title="최초 관리자 비밀번호 설정"
          style={{ width: 420, maxWidth: "100%" }}
        >
          {error && <Alert type="error" showIcon message={error} />}
          <Alert
            type="info"
            showIcon
            message="초대받은 관리자 계정에서 사용할 비밀번호를 설정해 주세요."
          />
          <Form
            layout="vertical"
            onFinish={setInitialPassword}
            style={{ marginTop: 20 }}
          >
            <Form.Item
              name="password"
              label="새 비밀번호"
              rules={[
                { required: true, message: "새 비밀번호를 입력해 주세요." },
                { min: 8, message: "비밀번호는 8자 이상이어야 합니다." },
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              name="passwordConfirmation"
              label="새 비밀번호 확인"
              dependencies={["password"]}
              rules={[
                { required: true, message: "새 비밀번호를 다시 입력해 주세요." },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    return !value || getFieldValue("password") === value
                      ? Promise.resolve()
                      : Promise.reject(new Error("비밀번호가 일치하지 않습니다."));
                  },
                }),
              ]}
            >
              <Input.Password autoComplete="new-password" />
            </Form.Item>
            <Button type="primary" htmlType="submit" block>
              비밀번호 설정
            </Button>
          </Form>
        </Card>
      </div>
    );
  if (context) return children(context, logout);
  return (
    <div className="auth-page">
      <Card
        title="학원 학생 관리 · 직원 로그인"
        style={{ width: 420, maxWidth: "100%" }}
      >
        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            action={<Button onClick={restore}>재시도</Button>}
          />
        )}
        <Form layout="vertical" onFinish={login} style={{ marginTop: 20 }}>
          <Form.Item
            name="email"
            label="이메일"
            rules={[
              {
                required: true,
                type: "email",
                message: "직원 이메일을 입력해 주세요.",
              },
            ]}
          >
            <Input autoComplete="username" />
          </Form.Item>
          <Form.Item
            name="password"
            label="비밀번호"
            rules={[{ required: true, message: "비밀번호를 입력해 주세요." }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" block disabled={!supabase}>
            로그인
          </Button>
        </Form>
      </Card>
    </div>
  );
}
